import { useCollection, useDocument } from "@/composables/useFirestore";
import { db } from "@/firebase";
import { useUserStore } from "@/store";
import {
  commentReaderFullPath,
  commentReadersPathFromAccount,
  commentsFullPath,
  commentTargetKey,
  type Comment,
  type CommentReader,
  type CommentTarget,
  type CommentTargetType,
} from "@aircap/common/comment";
import {
  and,
  collection,
  collectionGroup,
  doc,
  or,
  orderBy,
  query,
  where,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { computed, toValue, type MaybeRefOrGetter } from "vue";

/** A transfer request and its transaction share one thread: pass both as targets. */
export const useComments = (
  accountId: MaybeRefOrGetter<string>,
  targets: MaybeRefOrGetter<CommentTarget[]>,
) => {
  const { data: comments } = useCollection<Comment>(
    computed(() => {
      const thread = toValue(targets);
      if (!thread.length) return null;
      return query(
        collection<Comment>(db, commentsFullPath(toValue(accountId))),
        or(
          ...thread.map(({ targetType, targetId }) =>
            and(
              where<Comment>("targetType", "==", targetType),
              where<Comment>("targetId", "==", targetId),
            ),
          ),
        ),
        orderBy<Comment>("createdAt"),
      );
    }),
  );
  return { comments };
};

export const useUnreadComments = (accountId: MaybeRefOrGetter<string | undefined>) => {
  const user = useUserStore();
  const { data: reader } = useDocument<CommentReader>(
    computed(() => {
      const id = toValue(accountId);
      return user.uid && id ? doc(db, commentReaderFullPath(id, user.uid)) : null;
    }),
  );
  const unreadCount = (targets: CommentTarget[]) =>
    targets.reduce(
      (count, target) =>
        count + (reader.value?.unreadCommentIds[commentTargetKey(target)]?.length ?? 0),
      0,
    );
  const unreadCountOfType = (targetType: CommentTargetType) =>
    Object.entries(reader.value?.unreadCommentIds ?? {}).reduce(
      (count, [key, ids]) => (key.startsWith(`${targetType}-`) ? count + ids.length : count),
      0,
    );
  return { unreadCount, unreadCountOfType };
};

// Look accounts up in it, never iterate it: a reader stays on an account the user no longer sees.
export const useUnreadCommentsByAccount = () => {
  type AccountUnreadCount = { accountId: string | undefined; unreadCount: number };
  const user = useUserStore();
  const { data: readers } = useCollection<AccountUnreadCount>(
    computed(() =>
      user.uid && (user.isKind("admin") || user.isKind("supervisor"))
        ? query(
            collectionGroup(db, commentReadersPathFromAccount),
            where<{ reader: { uid: string } }>("reader.uid", "==", user.uid),
            where<CommentReader>("unreadCount", ">", 0),
          ).withConverter<AccountUnreadCount, CommentReader>({
            toFirestore: () => {
              throw new Error("Comment readers are written by the server only");
            },
            fromFirestore: (snapshot: QueryDocumentSnapshot<CommentReader, CommentReader>) => ({
              accountId: snapshot.ref.parent.parent?.id,
              unreadCount: snapshot.data().unreadCount,
            }),
          })
        : null,
    ),
  );
  return computed(
    () =>
      new Map((readers.value ?? []).map(({ accountId, unreadCount }) => [accountId, unreadCount])),
  );
};
